/**
 * Navigation Component
 * Main application header with responsive navigation, user menu, and notifications
 * Transparent on homepage, solid on scroll
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Tooltip,
  Typography,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
  Article as ArticleIcon,
  Mail as MailIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  PlayCircle as PlayCircleIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import NotificationDropdown from './NotificationDropdown';
import NovuNotificationBell from './NovuNotificationBell';

/**
 * Navigation component
 * Responsive header with desktop and mobile navigation
 */
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { notifications, markNotificationRead, markAllNotificationsRead } = useSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [scrollTrigger, setScrollTrigger] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Detect scroll to change navigation style
  useEffect(() => {
    const handleScroll = () => {
      setScrollTrigger(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    await logout();
    setUserMenuAnchor(null);
    navigate('/');
  };

  // Check if on homepage
  const isHomepage = location.pathname === '/';

  // "Home" routes to the user's dashboard when logged in, otherwise the landing page
  const homeRoute = user?.role === 'homeowner' ? '/dashboard'
    : user?.role === 'contractor' ? '/dashboard/contractor'
    : user?.role === 'admin' ? '/admin'
    : '/';

  // Unread notifications count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Navigation links for authenticated users
  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setUserMenuAnchor(null);   // always close the user dropdown on navigation
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: '#111111',
        color: '#F5F0E8',
        boxShadow: scrollTrigger || !isHomepage ? '0px 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backdropFilter: scrollTrigger || !isHomepage ? 'none' : 'blur(10px)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* ── Veteran Owned Badge (far left of navbar) ── */}
          <Box
            component="img"
            src="/veteran-owned-badge.png"
            alt="Veteran Owned Business"
            sx={{
              height: { xs: 44, md: 54 },
              width: 'auto',
              mr: 1.5,
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
              userSelect: 'none',
              pointerEvents: 'none',
              flexShrink: 0,
            }}
          />

          {/* Logo — always navigates to the public home page */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.03)' },
            }}
          >
            <img
              src="/tradewars-logo.svg"
              alt="TradeWars"
              style={{ height: '44px', width: 'auto' }}
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3, flex: 1, ml: 4 }}>
              <Button
                color="inherit"
                onClick={() => navigate(homeRoute)}
                sx={{
                  fontWeight: location.pathname === homeRoute ? 600 : 400,
                  borderBottom: location.pathname === homeRoute ? '2px solid #C9A84C' : 'none',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                Home
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate('/about')}
                sx={{
                  fontWeight: location.pathname === '/about' ? 600 : 400,
                  borderBottom: location.pathname === '/about' ? '2px solid #C9A84C' : 'none',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                About
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate('/leadership')}
                sx={{
                  fontWeight: location.pathname === '/leadership' ? 600 : 400,
                  borderBottom: location.pathname === '/leadership' ? '2px solid #C9A84C' : 'none',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                Leadership
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate('/how-it-works')}
                startIcon={<PlayCircleIcon sx={{ color: '#C9A84C' }} />}
                sx={{
                  fontWeight: location.pathname === '/how-it-works' ? 600 : 400,
                  borderBottom: location.pathname === '/how-it-works' ? '2px solid #C9A84C' : 'none',
                  '&:hover': { color: '#C9A84C' },
                }}
              >
                How It Works
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate('/contact')}
                sx={{
                  fontWeight: location.pathname === '/contact' ? 600 : 400,
                  borderBottom: location.pathname === '/contact' ? '2px solid #C9A84C' : 'none',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                Contact
              </Button>
              <Button
                onClick={() => navigate('/launch')}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: '#fff',
                  backgroundColor: '#FF6B35',
                  borderRadius: 99,
                  px: 2,
                  py: 0.5,
                  ml: 0.5,
                  animation: 'navPulse 2.5s ease-in-out infinite',
                  '@keyframes navPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(255,107,53,0.5)' },
                    '50%': { boxShadow: '0 0 0 6px rgba(255,107,53,0)' },
                  },
                  '&:hover': { backgroundColor: '#e55100' },
                }}
              >
                🚀 Launch Day
              </Button>
            </Box>
          )}

          {/* Desktop Auth Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAuthenticated ? (
              <>
                {/* Novu in-app notification bell (active when REACT_APP_NOVU_APP_ID is set) */}
                <NovuNotificationBell />

                {/* Existing socket-based notifications bell */}
                <Tooltip title="Notifications">
                  <IconButton
                    color="inherit"
                    onClick={(e) => setNotificationAnchor(e.currentTarget)}
                    sx={{
                      '&:hover': { color: '#B71C1C' },
                    }}
                  >
                    <Badge badgeContent={unreadCount} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* User Avatar Menu */}
                <Tooltip title={user?.name || 'User'}>
                  <IconButton
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    sx={{ p: 0 }}
                  >
                    <Avatar
                      alt={user?.name}
                      src={user?.avatar}
                      sx={{ width: 36, height: 36, bgcolor: '#FF6B35' }}
                    >
                      {user?.name?.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>

                {/* User Menu */}
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={() => setUserMenuAnchor(null)}
                >
                  {['homeowner','admin'].includes(user?.role) && (
                    <>
                      <MenuItem onClick={() => handleNavigation('/submit-project')}>
                        <ListItemIcon>
                          <HomeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Submit Project</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => handleNavigation('/dashboard/homeowner')}>
                        <ListItemIcon>
                          <DashboardIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Dashboard</ListItemText>
                      </MenuItem>
                    </>
                  )}

                  {user?.role === 'contractor' && (
                    <>
                      <MenuItem onClick={() => handleNavigation('/dashboard/contractor')}>
                        <ListItemIcon>
                          <DashboardIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Dashboard</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => handleNavigation('/rfps')}>
                        <ListItemIcon>
                          <BusinessIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Browse Bid Requests</ListItemText>
                      </MenuItem>
                    </>
                  )}

                  {user?.role === 'admin' && (
                    <MenuItem onClick={() => handleNavigation('/admin')}>
                      <ListItemIcon>
                        <AdminIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Admin Panel</ListItemText>
                    </MenuItem>
                  )}

                  <Divider />
                  <MenuItem onClick={() => handleNavigation('/how-it-works')}>
                    <ListItemIcon>
                      <HelpOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Help & How It Works</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleNavigation('/account')}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Account Settings</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              !isMobile && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/login')}
                    sx={{
                      borderColor: '#B71C1C',
                      color: '#B71C1C',
                      '&:hover': {
                        borderColor: '#7F0000',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{
                      backgroundColor: '#FF6B35',
                      '&:hover': { backgroundColor: '#E55100' },
                    }}
                  >
                    Register
                  </Button>
                </Box>
              )
            )}

            {/* Mobile Menu Toggle */}
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 280, pt: 2 }}>
          <List>
            <ListItem button onClick={() => handleNavigation(homeRoute)}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/about')}>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText primary="About" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/leadership')}>
              <ListItemIcon>
                <GroupsIcon />
              </ListItemIcon>
              <ListItemText primary="Leadership" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/how-it-works')}>
              <ListItemIcon>
                <PlayCircleIcon sx={{ color: '#C9A84C' }} />
              </ListItemIcon>
              <ListItemText primary="How It Works" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/contact')}>
              <ListItemIcon>
                <MailIcon />
              </ListItemIcon>
              <ListItemText primary="Contact" />
            </ListItem>
            <ListItem
              button
              onClick={() => handleNavigation('/launch')}
              sx={{
                backgroundColor: '#FF6B3515',
                borderRadius: 2,
                mx: 1,
                mb: 1,
              }}
            >
              <ListItemIcon>
                <span style={{ fontSize: 20 }}>🚀</span>
              </ListItemIcon>
              <ListItemText
                primary="Launch Day — June 1"
                primaryTypographyProps={{ fontWeight: 700, color: '#FF6B35' }}
              />
            </ListItem>

            {isAuthenticated && (
              <>
                <Divider sx={{ my: 1 }} />
                {['homeowner','admin'].includes(user?.role) && (
                  <>
                    <ListItem button onClick={() => handleNavigation('/submit-project')}>
                      <ListItemIcon>
                        <HomeIcon />
                      </ListItemIcon>
                      <ListItemText primary="Submit Project" />
                    </ListItem>
                    <ListItem button onClick={() => handleNavigation('/dashboard/homeowner')}>
                      <ListItemIcon>
                        <DashboardIcon />
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" />
                    </ListItem>
                  </>
                )}
                {user?.role === 'contractor' && (
                  <>
                    <ListItem button onClick={() => handleNavigation('/dashboard/contractor')}>
                      <ListItemIcon>
                        <DashboardIcon />
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" />
                    </ListItem>
                  </>
                )}
                {user?.role === 'admin' && (
                  <ListItem button onClick={() => handleNavigation('/admin')}>
                    <ListItemIcon>
                      <AdminIcon />
                    </ListItemIcon>
                    <ListItemText primary="Admin Panel" />
                  </ListItem>
                )}
                <ListItem button onClick={() => handleNavigation('/account')}>
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Account Settings" />
                </ListItem>
                <ListItem button onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItem>
              </>
            )}

            {!isAuthenticated && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem button onClick={() => handleNavigation('/login')}>
                  <ListItemText primary="Login" />
                </ListItem>
                <ListItem button onClick={() => handleNavigation('/register')}>
                  <ListItemText primary="Register" />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Notification Dropdown */}
      <NotificationDropdown
        notifications={notifications}
        anchorEl={notificationAnchor}
        onClose={() => setNotificationAnchor(null)}
        onMarkRead={markNotificationRead}
        onMarkAllRead={markAllNotificationsRead}
      />

      {/* How It Works Video Modal */}
      <Dialog
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            bgcolor: '#111111',
            color: '#F5F0E8',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#C9A84C' }}>
              How MyTradeWar Works
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Powered by AI. Built for transparency and fair competition.
            </Typography>
          </Box>
          <IconButton onClick={() => setVideoModalOpen(false)} sx={{ color: '#F5F0E8', mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* How It Works Video */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '56.25%',
              bgcolor: '#000',
              borderBottom: '1px solid #333',
            }}
          >
            <Box
              component="iframe"
              src="https://player.vimeo.com/video/1177899463?autoplay=1&title=0&byline=0&portrait=0"
              sx={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </Box>

          {/* Two-Path Description */}
          <Box sx={{ p: 3 }}>

            {/* For Homeowners */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 4, height: 32, bgcolor: '#C9A84C', borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#C9A84C' }}>
                  For Homeowners
                </Typography>
              </Box>

              {[
                {
                  step: '1',
                  title: 'Post Your Project — It\'s Free',
                  desc: 'Describe what you need done in your own words — even if it\'s rough or incomplete. Qualified local contractors will submit real, competitive binding bids directly to you. No signup fees. No hidden costs. Posting is always free.',
                },
                {
                  step: '2',
                  title: 'Receive Competitive Bids',
                  desc: 'Qualified local professionals review your project and submit their best offers. You\'ll see every bid side-by-side — including price, timeline, and contractor profile — making it easy to compare apples-to-apples.',
                },
                {
                  step: '3',
                  title: 'Choose Your Contractor',
                  desc: 'Review contractor ratings, past project feedback, and verified license & insurance badges. Select the bid that best fits your budget and timeline. You remain in full control with no high-pressure sales tactics.',
                },
                {
                  step: '4',
                  title: 'Pay into Secure Escrow',
                  desc: 'Once you accept a bid, our AI generates a professional contract locking in the agreed scope, timeline, and price — no ambiguity, no surprises. Your payment is then held safely in escrow powered by Stripe. The contractor cannot access any funds until you approve the completed work.',
                },
                {
                  step: '5',
                  title: 'Approve & Release Payment',
                  desc: 'When the job is finished and you\'re 100% satisfied, simply click "Approve Work" to release the funds instantly. If anything isn\'t right, your money stays protected until the issue is resolved.',
                },
              ].map(({ step, title, desc }) => (
                <Box key={step} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      bgcolor: '#C9A84C', color: '#111',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.8rem', flexShrink: 0, mt: 0.2,
                    }}
                  >
                    {step}
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#F5F0E8', mb: 0.3 }}>
                      {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#aaa', lineHeight: 1.6 }}>
                      {desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Divider */}
            <Box sx={{ borderTop: '1px solid #2a2a2a', mb: 4 }} />

            {/* For Contractors */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 4, height: 32, bgcolor: '#B71C1C', borderRadius: 1, flexShrink: 0 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#B71C1C' }}>
                  For Contractors
                </Typography>
              </Box>

              {[
                {
                  step: '1',
                  title: 'Create Your Profile & Join the Network',
                  desc: 'Sign up and build your contractor profile. For licensed trades — electrical, plumbing, HVAC, roofing, and more — submit your license, insurance, and bonding to earn a Verified Professional badge. For general services like lawn care, handyman work, or painting, complete your profile and get started right away. Your reputation grows through completed jobs and homeowner reviews.',
                },
                {
                  step: '2',
                  title: 'Browse Open Projects',
                  desc: 'See every new project in your service area the moment it\'s posted. No gatekeepers. No expensive lead fees. All professionals on the platform have equal, real-time access to opportunities that match their trade.',
                },
                {
                  step: '3',
                  title: 'Submit a Bid',
                  desc: 'Review the homeowner\'s project details and submit your competitive price, timeline, and any relevant notes. No lead fees, no competing blind — you see the full project before you commit to a number.',
                },
                {
                  step: '4',
                  title: 'Get Selected & Start Work',
                  desc: 'When a homeowner accepts your bid, our AI generates a professional contract locking in the agreed scope, price, and timeline — protecting you as much as the homeowner. The job is already funded in escrow, so you know the money is secure before you begin any work.',
                },
                {
                  step: '5',
                  title: 'Complete the Job & Get Paid',
                  desc: 'Finish the work, wait for homeowner approval, and get paid instantly via Stripe. Fast, transparent payments with no chasing invoices.',
                },
              ].map(({ step, title, desc }) => (
                <Box key={step} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      bgcolor: '#B71C1C', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.8rem', flexShrink: 0, mt: 0.2,
                    }}
                  >
                    {step}
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#F5F0E8', mb: 0.3 }}>
                      {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#aaa', lineHeight: 1.6 }}>
                      {desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

          </Box>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Navigation;
